import { collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot, serverTimestamp, arrayUnion, getDoc, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { getUserWorkspace } from './firestore';

const COLLECTION = 'zoom_accounts';

// ── Multi-tenant workspace ──
let _wsId = null;
export const setZoomWorkspace = async (userId) => { if (userId) _wsId = await getUserWorkspace(userId); };
const withWs = (d) => ({ ...d, workspaceId: _wsId || 'default' });
const wsQ = (colRef, ...c) => _wsId ? query(colRef, where('workspaceId', '==', _wsId), ...c) : query(colRef, ...c);

// Subscribe to zoom accounts (workspace filtered)
export const subscribeToZoomAccounts = (callback) => {
  const q = wsQ(collection(db, COLLECTION));
  return onSnapshot(q, (snapshot) => {
    const accounts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(accounts);
  }, error => {
    console.error('Error subscribing to zoom accounts:', error);
    callback([]);
  });
};

// Create a new zoom account
export const createZoomAccount = async (data) => {
  const docRef = await addDoc(collection(db, COLLECTION), withWs({
    name: data.name,
    email: data.email,
    color: data.color || '#3B82F6',
    meetings: [],
    deletedMeetings: [],
    claseMeetings: [],
    deletedClassMeetings: [],
    createdAt: serverTimestamp()
  }));
  return docRef.id;
};

// Update a zoom account (name, email, color)
export const updateZoomAccount = async (accountId, data) => {
  await updateDoc(doc(db, COLLECTION, accountId), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

// Delete a zoom account
export const deleteZoomAccount = async (accountId) => {
  await deleteDoc(doc(db, COLLECTION, accountId));
};

// ─── MASTERCLASS MEETINGS ────────────────────────────────────────────────────

export const addMeetingToAccount = async (accountId, _account, meeting) => {
  const newMeeting = {
    id: `meeting-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: meeting.name,
    zoomLink: meeting.zoomLink,
    meetingId: meeting.meetingId,
    password: meeting.password,
    createdAt: new Date().toISOString()
  };
  await updateDoc(doc(db, COLLECTION, accountId), {
    meetings: arrayUnion(newMeeting),
    updatedAt: serverTimestamp()
  });
  return newMeeting;
};

export const updateMeetingInAccount = async (accountId, _account, meetingId, data) => {
  const snap = await getDoc(doc(db, COLLECTION, accountId));
  if (!snap.exists()) throw new Error('Account not found');
  const updatedMeetings = (snap.data().meetings || []).map(m => m.id === meetingId ? { ...m, ...data } : m);
  await updateDoc(doc(db, COLLECTION, accountId), { meetings: updatedMeetings, updatedAt: serverTimestamp() });
};

export const deleteMeetingFromAccount = async (accountId, _account, meetingId) => {
  const snap = await getDoc(doc(db, COLLECTION, accountId));
  if (!snap.exists()) throw new Error('Account not found');
  const data = snap.data();
  const deleted = (data.meetings || []).find(m => m.id === meetingId);
  const remaining = (data.meetings || []).filter(m => m.id !== meetingId);
  const updateData = { meetings: remaining, updatedAt: serverTimestamp() };
  if (deleted) updateData.deletedMeetings = arrayUnion({ ...deleted, deletedAt: new Date().toISOString() });
  await updateDoc(doc(db, COLLECTION, accountId), updateData);
};

export const restoreMeetingFromTrash = async (accountId, meetingId) => {
  const snap = await getDoc(doc(db, COLLECTION, accountId));
  if (!snap.exists()) throw new Error('Account not found');
  const data = snap.data();
  const deletedMeetings = data.deletedMeetings || [];
  const toRestore = deletedMeetings.find(m => m.id === meetingId);
  if (!toRestore) throw new Error('Meeting not found in trash');
  const remaining = deletedMeetings.filter(m => m.id !== meetingId);
  const { deletedAt, ...restoredMeeting } = toRestore;
  await updateDoc(doc(db, COLLECTION, accountId), {
    meetings: arrayUnion(restoredMeeting),
    deletedMeetings: remaining,
    updatedAt: serverTimestamp()
  });
};

export const permanentlyDeleteMeeting = async (accountId, meetingId) => {
  const snap = await getDoc(doc(db, COLLECTION, accountId));
  if (!snap.exists()) throw new Error('Account not found');
  const remaining = (snap.data().deletedMeetings || []).filter(m => m.id !== meetingId);
  await updateDoc(doc(db, COLLECTION, accountId), { deletedMeetings: remaining, updatedAt: serverTimestamp() });
};

export const emptyTrashForAccount = async (accountId) => {
  await updateDoc(doc(db, COLLECTION, accountId), { deletedMeetings: [], updatedAt: serverTimestamp() });
};

// ─── CLASE MEETINGS ──────────────────────────────────────────────────────────

export const addClaseMeetingToAccount = async (accountId, meeting) => {
  const newMeeting = {
    id: `clase-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: meeting.name,
    zoomLink: meeting.zoomLink,
    meetingId: meeting.meetingId,
    password: meeting.password,
    createdAt: new Date().toISOString()
  };
  await updateDoc(doc(db, COLLECTION, accountId), {
    claseMeetings: arrayUnion(newMeeting),
    updatedAt: serverTimestamp()
  });
  return newMeeting;
};

export const updateClaseMeetingInAccount = async (accountId, meetingId, data) => {
  const snap = await getDoc(doc(db, COLLECTION, accountId));
  if (!snap.exists()) throw new Error('Account not found');
  const updated = (snap.data().claseMeetings || []).map(m => m.id === meetingId ? { ...m, ...data } : m);
  await updateDoc(doc(db, COLLECTION, accountId), { claseMeetings: updated, updatedAt: serverTimestamp() });
};

export const deleteClaseMeetingFromAccount = async (accountId, meetingId) => {
  const snap = await getDoc(doc(db, COLLECTION, accountId));
  if (!snap.exists()) throw new Error('Account not found');
  const data = snap.data();
  const found = (data.claseMeetings || []).find(m => m.id === meetingId);
  const remaining = (data.claseMeetings || []).filter(m => m.id !== meetingId);
  const updateData = { claseMeetings: remaining, updatedAt: serverTimestamp() };
  if (found) updateData.deletedClassMeetings = arrayUnion({ ...found, deletedAt: new Date().toISOString() });
  await updateDoc(doc(db, COLLECTION, accountId), updateData);
};

export const restoreClassMeetingFromTrash = async (accountId, meetingId) => {
  const snap = await getDoc(doc(db, COLLECTION, accountId));
  if (!snap.exists()) throw new Error('Account not found');
  const data = snap.data();
  const deleted = data.deletedClassMeetings || [];
  const toRestore = deleted.find(m => m.id === meetingId);
  if (!toRestore) throw new Error('Clase meeting not found in trash');
  const remaining = deleted.filter(m => m.id !== meetingId);
  const { deletedAt, ...restored } = toRestore;
  await updateDoc(doc(db, COLLECTION, accountId), {
    claseMeetings: arrayUnion(restored),
    deletedClassMeetings: remaining,
    updatedAt: serverTimestamp()
  });
};

export const permanentlyDeleteClassMeeting = async (accountId, meetingId) => {
  const snap = await getDoc(doc(db, COLLECTION, accountId));
  if (!snap.exists()) throw new Error('Account not found');
  const remaining = (snap.data().deletedClassMeetings || []).filter(m => m.id !== meetingId);
  await updateDoc(doc(db, COLLECTION, accountId), { deletedClassMeetings: remaining, updatedAt: serverTimestamp() });
};

export const emptyClassTrashForAccount = async (accountId) => {
  await updateDoc(doc(db, COLLECTION, accountId), { deletedClassMeetings: [], updatedAt: serverTimestamp() });
};

// ─── UTILITIES ───────────────────────────────────────────────────────────────

export const duplicateZoomAccount = async (accountId) => {
  const snap = await getDoc(doc(db, COLLECTION, accountId));
  if (!snap.exists()) throw new Error('Account not found');
  const data = snap.data();

  // Quitar los vínculos con Zoom/curso: dos cuentas no deben reclamar la misma sala creada por el Gestor.
  const cloneMeetings = (arr) => (arr || []).map(m => {
    const { courseId, source, zoomNumericId, zoomMissing, lastSyncedAt, ...rest } = m;
    return {
      ...rest,
      id: `meeting-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      createdAt: new Date().toISOString()
    };
  });

  const docRef = await addDoc(collection(db, COLLECTION), {
    name: `${data.name} (copia)`,
    email: '',
    color: data.color || '#3B82F6',
    meetings: cloneMeetings(data.meetings),
    deletedMeetings: [],
    claseMeetings: cloneMeetings(data.claseMeetings),
    deletedClassMeetings: [],
    createdAt: serverTimestamp()
  });
  return docRef.id;
};
